import moment from "moment";
import React from "react";
import { Card } from "react-bootstrap";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

// Utils
import { getFileExtension } from "../../utils/fileUtils";
const baseURL = process.env.REACT_APP_API;

/**
 * Renderiza un mensaje de texto
 *
 * @param {String} text - Texto del mensaje
 * @returns {JSX.Element} - Componente de React
 */
export const TextMessage = ({ text }) => {
  // Verifica si el texto es una URL
  const isUrl = (text) => {
    try {
      new URL(text);
      return true;
    } catch (e) {
      return false;
    }
  };

  return (
    <p
      className="text-secondary font-14"
      align="left"
      style={{
        minWidth: "150px",
        maxWidth: "300px",
        whiteSpace: "pre-wrap",
      }}
    >
      {isUrl(text) ? (
        <a href={text} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      ) : (
        text
      )}
    </p>
  );
};

/**
 * Renderiza un mensaje de imagen
 *
 * @param {String} message - Mensaje de imagen
 * @param {String} alt - Texto alternativo de la imagen
 * @param {Function} callBack - Función para abrir el modal
 * @returns {JSX.Element} Componente de React
 */
export const ImageMessage = ({ message, alt, callBack }) => {
  return (
    <>
      <div className="cursor-pointer d-flex flex-column align-items-center ">
        <LazyLoadImage
          src={
            message.object.includes("http")
              ? message.object
              : `${baseURL}/${message.object}`
          }
          alt={alt}
          className="my-1"
          style={{
            backgroundSize: "cover",
            maxHeight: "220px",
            height: "auto",
            maxWidth: "100%",
          }}
          onClick={callBack}
          effect="blur"
        />
      </div>

      {message.coment && (
        <p
          className="text-secondary font-14"
          align="left"
          style={{
            whiteSpace: "pre-wrap",
            maxWidth: "100%",
          }}
        >
          {message.coment}
        </p>
      )}
    </>
  );
};

/**
 * Renderiza un mensaje de archivo
 *
 * @param {Object} message - Mensaje de archivo
 * @param {String} message.object - URL del archivo
 * @param {String} message.coment - Comentario del archivo
 * @param {String} date - Fecha del mensaje
 * @returns {JSX.Element} Componente de React
 */
export const FileMessage = ({ message, date }) => {
  return (
    <>
      <div className="d-flex flex-column align-items-center">
        <Card
          className="cursor-pointer border border-light text-muted fw-bold my-1 p-1"
          style={{
            minWidth: "250px",
            maxWidth: "300px",
          }}
          onClick={() => {
            window.open(`${baseURL}/${message.object}`);
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <div className="avatar-sm">
              <span className="avatar-title rounded">
                <i className="mdi mdi-file-document font-20" />
              </span>
            </div>

            <h5 className="text-secondary font-14 my-0">
              {`${moment(date).format("DDMMYYYY")}.${getFileExtension(
                message.object
              )}`}
            </h5>
          </div>
        </Card>
      </div>

      {message.coment && (
        <p
          className="text-secondary font-14"
          align="left"
          style={{
            whiteSpace: "pre-wrap",
            maxWidth: "100%",
          }}
        >
          {message.coment}
        </p>
      )}
    </>
  );
};

/**
 * Renderiza un mensaje de audio
 *
 * @param {Object} message - Mensaje de audio
 * @param {String} message.object - URL del audio
 * @param {String} message.coment - Comentario del audio
 * @returns {JSX.Element} Componente de React
 */
export const AudioMessage = ({ message }) => {
  return (
    <>
      <div className="d-flex flex-column align-items-center">
        <div className="d-flex align-items-center py-1">
          <audio controls className="border border-secondary rounded-pill">
            <source src={`${baseURL}/${message.object}`} type="audio/mpeg" />
            Tu navegador no soporta audio.
          </audio>
        </div>
      </div>

      {message.coment && (
        <p
          className="text-secondary font-14"
          align="left"
          style={{
            whiteSpace: "pre-wrap",
          }}
        >
          {message.coment}
        </p>
      )}
    </>
  );
};

/**
 * Renderiza un mensaje de video
 *
 * @param {Object} message - Mensaje de video
 * @param {String} message.object - URL del video
 * @param {String} message.coment - Comentario del video
 * @param {Function} callBack - Función para abrir el modal
 * @returns {JSX.Element} Componente de React
 */
export const VideoMessage = ({ message, callBack }) => {
  return (
    <>
      <div
        className="cursor-pointer d-flex align-items-center rounded my-1"
        onClick={callBack}
      >
        <video
          height="180"
          className="rounded"
          style={{
            maxWidth: 320,
            minWidth: 120,
          }}
        >
          <source src={`${baseURL}/${message.object}`} type="video/mp4" />
          Tu navegador no soporta video.
        </video>

        <div
          className="position-absolute translate-middle"
          style={{
            top: "45%",
            left: "50%",
          }}
        >
          <i
            className="mdi mdi-play-circle-outline text-white h2"
            style={{
              fontSize: "50px",
            }}
          />
        </div>
      </div>

      {message.coment && (
        <p
          className="text-secondary font-14"
          align="left"
          style={{
            whiteSpace: "pre-wrap",
          }}
        >
          {message.coment}
        </p>
      )}
    </>
  );
};

/**
 * Renderiza un mensaje de sticker
 *
 * @param {Object} message - Mensaje de sticker
 * @param {String} message.object - URL del sticker
 * @returns {JSX.Element} Componente de React
 */
export const StickerMessage = ({ message }) => {
  return (
    <img
      src={`${baseURL}/${message.object}`}
      alt="Sticker"
      className="rounded my-1"
      style={{
        maxHeight: "100px",
        maxWidth: "100px",
        backgroundSize: "cover",
      }}
    />
  );
};

/**
 * Renderiza un mensaje de contacto
 *
 * @param {String} nombre - Nombre del contacto
 * @param {String} telefono - Teléfono del contacto
 * @returns {JSX.Element} Componente de React
 */
const ContactCard = ({ nombre, telefono }) => (
  <Card
    className="cursor-pointer border border-light text-muted fw-bold my-1 p-1"
    style={{
      minWidth: "200px",
    }}
    onClick={() => {
      navigator.clipboard.writeText(telefono);
    }}
  >
    <div className="d-flex align-items-center gap-2">
      <div className="avatar-sm">
        <span className="avatar-title rounded">
          <i className="mdi mdi-account font-20" />
        </span>
      </div>

      <div className="d-flex flex-column">
        <h5 className="text-secondary font-14 my-0">{nombre}</h5>
        <p className="text-secondary font-12 my-0">{telefono}</p>
      </div>
    </div>
  </Card>
);

/**
 * Renderiza un mensaje de contacto
 *
 * @param {String} contact - Información del contacto
 * @returns {JSX.Element} Componente de React
 */
export const ContactMessage = ({ contact }) => {
  const contacts = JSON.parse(contact);

  return (
    <div>
      {Array.isArray(contacts) ? (
        // Iterar sobre los contactos si es una lista
        contacts.map((contact, index) => (
          <ContactCard key={index} {...contact} />
        ))
      ) : (
        // Si no es una lista, solo mostrar un contacto
        <ContactCard {...contacts} />
      )}
    </div>
  );
};

/**
 * Renderiza un mensaje de ubicación
 *
 * @param {String} location - Ubicación del mensaje
 * @param {String} location.latitude - Latitud de la ubicación
 * @param {String} location.longitude - Longitud de la ubicación
 * @returns {JSX.Element} Componente de React
 */
export const LocationMessage = ({ location }) => {
  const [latitude, longitude] = location.split(" ");

  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;

  return (
    <Card
      className="cursor-pointer border border-light text-muted fw-bold my-1 p-1"
      style={{
        minWidth: "200px",
      }}
      onClick={() => {
        window.open(googleMapsUrl);
      }}
    >
      <div className="d-flex align-items-center gap-2">
        <div className="avatar-sm">
          <span className="avatar-title rounded">
            <i className="mdi mdi-map-marker font-20" />
          </span>
        </div>

        <h5 className="text-secondary font-14 my-0">Ver ubicación</h5>
      </div>
    </Card>
  );
};
